import clsx from "clsx";
import styles from "./StatusTag.module.scss";
import CheckFilledIcon from "@medoma/components/icons/CheckFilledIcon";
import ArrowRightFilledIcon from "@medoma/components/icons/ArrowRightFilledIcon";
import type { IStatus } from "./statusTagUtils";
import { statusDictionary } from "./statusTagUtils";
import { useLingui } from "@lingui/react/macro";
import { humanFormat } from "@medoma/models/date-and-time";

type IStatusTag = {
  status: IStatus;
  variant?:
    | "icon"
    | "icon-with-timestamp"
    | "icon-and-text"
    | "icon-and-text-with-timestamp";
  size?: "tiny" | "small" | "medium";
  timestamp?: Date | null;
};

const Icon = ({ status }: { status: IStatus }) => (
  <div className={styles.iconWrapper}>
    <div className={clsx(styles.icon, styles[`icon-${status}`])}>
      {status === "travellingTo" ? <ArrowRightFilledIcon /> : null}
      {status === "finished" ? <CheckFilledIcon /> : null}
    </div>
  </div>
);

export const StatusTag = ({
  status,
  variant = "icon",
  size = "small",
  timestamp,
}: IStatusTag) => {
  const { t } = useLingui();
  const label = t(statusDictionary[status]);

  if (variant === "icon") {
    return (
      <div
        aria-label={label}
        className={clsx(
          styles.statusTag,
          styles[`variant-${variant}`],
          styles[`color-${status}`],
          styles[`size-${size}`],
        )}
      >
        <Icon status={status} />
      </div>
    );
  }

  if (variant === "icon-with-timestamp") {
    return (
      <div
        aria-label={`${label}${timestamp ? `, ${humanFormat(timestamp, { timeStyle: "short" })}` : ""}`}
        className={clsx(
          styles.statusTag,
          styles[`variant-${variant}`],
          styles[`color-${status}`],
          styles[`size-${size}`],
        )}
      >
        <Icon status={status} />
        {timestamp ? (
          <div className={styles.text}>
            {humanFormat(timestamp, { timeStyle: "short" })}
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div
      aria-label={`${label}${timestamp ? `, ${humanFormat(timestamp, { timeStyle: "short" })}` : ""}`}
      className={clsx(
        styles.statusTag,
        styles[`variant-${variant}`],
        styles[`color-${status}`],
        styles[`size-${size}`],
      )}
    >
      <Icon status={status} />
      <div className={styles.text}>
        <span>{label}</span>
        {timestamp ? (
          <span>{humanFormat(timestamp, { timeStyle: "short" })}</span>
        ) : null}
      </div>
    </div>
  );
};
